// ── Financials Report ─────────────────────────────────────────────────────────
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { adminApi } from '../services/api';
import { fmtDate, fmtCurrency, statusClass, exportToExcel, daysAgoISO, todayISO } from '../utils/helpers';
import TopNav from '../components/layout/TopNav';
import { useReportFilters } from '../context/ReportFiltersContext';

export function FinancialsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { finFilters, setFinFilters } = useReportFilters();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const defaultFinFilters = { closeFrom: daysAgoISO(30), closeTo: todayISO(), revMode: 'na', revFrom: '', revTo: '', payeeMode: 'all', paidMode: 'all', paidFrom: '', paidTo: '' };
  const [filters, setFilters] = useState(finFilters || defaultFinFilters);
  const [appliedFilters, setAppliedFilters] = useState(finFilters || defaultFinFilters);

  function setF(k, v) { setFilters(f => ({ ...f, [k]: v })); }

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { closingFrom: appliedFilters.closeFrom, closingTo: appliedFilters.closeTo };
      if (appliedFilters.revMode !== 'all') { params.revMode = appliedFilters.revMode; params.revFrom = appliedFilters.revFrom; params.revTo = appliedFilters.revTo; }
      if (appliedFilters.paidMode !== 'all') { params.paidMode = appliedFilters.paidMode; params.paidFrom = appliedFilters.paidFrom; params.paidTo = appliedFilters.paidTo; }
      let data = await adminApi.getFinancials(params);
      if (appliedFilters.payeeMode === 'notEmpty') data = data.filter(r => r.payeeName);
      setRows(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [appliedFilters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  function handleApply() {
    setFinFilters({ ...filters });
    setAppliedFilters({ ...filters });
  }

  function handleReset() {
    setFinFilters(null);
    setFilters(defaultFinFilters);
    setAppliedFilters({ ...defaultFinFilters });
  }

  function goToOrder(orderId) {
    setFinFilters({ ...filters });
    navigate(`/orders/${orderId}`, { state: { from: location.pathname } });
  }

  async function handleExport() {
    const data = [['File #', 'Loan Number', 'Last Name', 'Lender', 'Broker', 'State', 'Closing Date', 'Status', 'Revenue', 'Revenue Date', 'Payee', 'Payee Amount', 'Paid Date']];
    rows.forEach(r => {
      data.push([
        `#${r.fileNumber}`, r.loanNumber||'', r.lastName||'', r.lender||'', r.broker||'',
        r.state||'', r.closingDate ? fmtDate(r.closingDate) : '', r.statusName||'',
        r.revenue||'', r.revDate ? fmtDate(r.revDate) : 'N/A',
        r.payeeName||'', r.payeeAmount||'',
        r.payeePaidDate ? fmtDate(r.payeePaidDate) : (r.payeeName ? 'N/A' : ''),
      ]);
    });
    await exportToExcel(data, 'Financials', 'Financials');
  }

  return (
    <div>
      <TopNav />
      <div className="page-content">
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: '1.25rem', flexWrap: 'wrap', gap: 12 }}>
          <h2>Financials — closed files</h2>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
            <FilterGroup label="Closing date">
              <input type="date" value={filters.closeFrom} onChange={e => setF('closeFrom', e.target.value)} style={{ width: 140, fontSize: 12, padding: '5px 8px' }} />
              <span style={{ fontSize: 12, color: '#888' }}>to</span>
              <input type="date" value={filters.closeTo} onChange={e => setF('closeTo', e.target.value)} style={{ width: 140, fontSize: 12, padding: '5px 8px' }} />
            </FilterGroup>
            <FilterGroup label="Revenue date">
              <select value={filters.revMode} onChange={e => setF('revMode', e.target.value)} style={{ fontSize: 12, padding: '5px 8px', width: 'auto' }}>
                <option value="all">All</option><option value="range">Date range</option><option value="na">N/A only</option>
              </select>
              {filters.revMode === 'range' && <>
                <input type="date" value={filters.revFrom} onChange={e => setF('revFrom', e.target.value)} style={{ width: 140, fontSize: 12, padding: '5px 8px' }} />
                <span style={{ fontSize: 12, color: '#888' }}>to</span>
                <input type="date" value={filters.revTo} onChange={e => setF('revTo', e.target.value)} style={{ width: 140, fontSize: 12, padding: '5px 8px' }} />
              </>}
            </FilterGroup>
            <FilterGroup label="Payee">
              <select value={filters.payeeMode} onChange={e => setF('payeeMode', e.target.value)} style={{ fontSize: 12, padding: '5px 8px', width: 'auto' }}>
                <option value="all">All</option><option value="notEmpty">Has payee</option>
              </select>
            </FilterGroup>
            <FilterGroup label="Paid date">
              <select value={filters.paidMode} onChange={e => setF('paidMode', e.target.value)} style={{ fontSize: 12, padding: '5px 8px', width: 'auto' }}>
                <option value="all">All</option><option value="range">Date range</option><option value="na">N/A only</option>
              </select>
              {filters.paidMode === 'range' && <>
                <input type="date" value={filters.paidFrom} onChange={e => setF('paidFrom', e.target.value)} style={{ width: 140, fontSize: 12, padding: '5px 8px' }} />
                <span style={{ fontSize: 12, color: '#888' }}>to</span>
                <input type="date" value={filters.paidTo} onChange={e => setF('paidTo', e.target.value)} style={{ width: 140, fontSize: 12, padding: '5px 8px' }} />
              </>}
            </FilterGroup>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary btn-sm" onClick={handleApply}>Apply</button>
              <button className="btn btn-sm" onClick={handleReset}>Reset</button>
              <button className="btn btn-sm" onClick={handleExport} style={{ display: 'flex', alignItems: 'center', gap: 5 }}><DownloadIcon /> Export</button>
            </div>
          </div>
        </div>

        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table className="report-table" style={{ minWidth: 1250 }}>
              <thead><tr>
                {['File #','Loan number','Last name','Lender','Broker','State','Closing date','Status','Revenue','Revenue date','Payee','Payee amount','Paid date'].map(h => <th key={h}>{h}</th>)}
              </tr></thead>
              <tbody>
                {loading ? <tr><td colSpan={13} style={{ padding: '2rem', textAlign: 'center', color: '#888' }}>Loading...</td></tr>
                : rows.length === 0 ? <tr><td colSpan={13} style={{ padding: '2rem', textAlign: 'center', color: '#aaa' }}>No closed files match the selected filters.</td></tr>
                : (() => {
                    let lastFile = null;
                    return rows.map((r, ri) => {
                      const isFirstForFile = r.fileNumber !== lastFile;
                      if (isFirstForFile) lastFile = r.fileNumber;
                      return (
                        <tr key={ri} onClick={() => goToOrder(r.orderId)} style={{ cursor: 'pointer', borderTop: isFirstForFile ? '2px solid #e0e4ec' : '1px dashed #f0f2f6' }}>
                          {isFirstForFile ? <>
                            <td style={{ fontWeight: 600, color: '#185FA5' }}>#{r.fileNumber}</td>
                            <td>{r.loanNumber||'—'}</td>
                            <td style={{ fontWeight: 500 }}>{r.lastName||'—'}</td>
                            <td>{r.lender||'—'}</td>
                            <td>{r.broker||'—'}</td>
                            <td>{r.state||'—'}</td>
                            <td>{fmtDate(r.closingDate)}</td>
                            <td><span className={`badge ${statusClass(r.statusName)}`}>{r.statusName}</span></td>
                            <td style={{ fontWeight: 500 }}>{fmtCurrency(r.revenue)}</td>
                            <td>{r.revDate ? fmtDate(r.revDate) : <span style={{ color: '#aaa' }}>N/A</span>}</td>
                          </> : <>{[...Array(10)].map((_, i) => <td key={i}></td>)}</>}
                          <td style={{ background: '#fafbfc', paddingLeft: !isFirstForFile ? 28 : undefined }}>
                            {r.payeeName ? <><span style={{ display: 'inline-block', width: 8, height: 8, background: r.payeePaidDate ? '#166534' : '#d0d5e0', borderRadius: '50%', marginRight: 5 }}></span>{r.payeeName}</> : '—'}
                          </td>
                          <td style={{ background: '#fafbfc' }}>{r.payeeAmount ? fmtCurrency(r.payeeAmount) : '—'}</td>
                          <td style={{ background: '#fafbfc' }}>{r.payeeName ? (r.payeePaidDate ? fmtDate(r.payeePaidDate) : <span style={{ color: '#aaa' }}>N/A</span>) : '—'}</td>
                        </tr>
                      );
                    });
                  })()}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Productivity Report ────────────────────────────────────────────────────────
export function ProductivityPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { prodFilters, setProdFilters } = useReportFilters();
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const defaultProdFilters = { from: daysAgoISO(30), to: todayISO(), userId: '' };
  const [filters, setFilters] = useState(prodFilters || defaultProdFilters);
  const [appliedFilters, setAppliedFilters] = useState(prodFilters || defaultProdFilters);

  function setF(k, v) { setFilters(f => ({ ...f, [k]: v })); }

  useEffect(() => {
    adminApi.getUsers().then(u => setUsers(u.filter(x => x.isActive))).catch(console.error);
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { dateFrom: appliedFilters.from, dateTo: appliedFilters.to };
      if (appliedFilters.userId) params.userId = appliedFilters.userId;
      setRows(await adminApi.getProductivity(params));
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [appliedFilters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  function handleApply() {
    setProdFilters({ ...filters });
    setAppliedFilters({ ...filters });
  }

  function handleReset() {
    setProdFilters(null);
    setFilters(defaultProdFilters);
    setAppliedFilters({ ...defaultProdFilters });
  }

  function goToOrder(orderId) {
    setProdFilters({ ...filters });
    navigate(`/orders/${orderId}`, { state: { from: location.pathname } });
  }

  async function handleExport() {
    const data = [['User','File #','Loan Number','Last Name','Lender','Broker','Note Date','Note']];
    rows.forEach(r => data.push([r.authorName, `#${r.fileNumber}`, r.loanNumber||'', r.lastName||'', r.lender||'', r.broker||'', r.noteDate, r.noteBody]));
    await exportToExcel(data, 'Productivity', 'Productivity');
  }

  return (
    <div>
      <TopNav />
      <div className="page-content">
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: '1.25rem', flexWrap: 'wrap', gap: 12 }}>
          <h2>Productivity — notes by date range</h2>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
            <FilterGroup label="Note date range">
              <input type="date" value={filters.from} onChange={e => setF('from', e.target.value)} style={{ width: 140, fontSize: 12, padding: '5px 8px' }} />
              <span style={{ fontSize: 12, color: '#888' }}>to</span>
              <input type="date" value={filters.to} onChange={e => setF('to', e.target.value)} style={{ width: 140, fontSize: 12, padding: '5px 8px' }} />
            </FilterGroup>
            <FilterGroup label="User">
              <select value={filters.userId} onChange={e => setF('userId', e.target.value)} style={{ fontSize: 12, padding: '5px 10px', minWidth: 160, width: 'auto' }}>
                <option value="">All users</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.fullName}</option>)}
              </select>
            </FilterGroup>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary btn-sm" onClick={handleApply}>Apply</button>
              <button className="btn btn-sm" onClick={handleReset}>Reset</button>
              <button className="btn btn-sm" onClick={handleExport} style={{ display: 'flex', alignItems: 'center', gap: 5 }}><DownloadIcon /> Export</button>
            </div>
          </div>
        </div>

        <div style={{ fontSize: 13, color: '#666', marginBottom: '0.75rem' }}>
          <strong>{rows.length}</strong> note{rows.length !== 1 ? 's' : ''} found
        </div>

        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table className="report-table">
              <thead><tr>
                <th style={{ minWidth: 140 }}>User</th><th>File #</th><th>Loan number</th>
                <th>Last name</th><th>Lender</th><th>Broker</th><th>Note date</th><th>Note</th>
              </tr></thead>
              <tbody>
                {loading ? <tr><td colSpan={8} style={{ padding: '2rem', textAlign: 'center', color: '#888' }}>Loading...</td></tr>
                : rows.length === 0 ? <tr><td colSpan={8} style={{ padding: '2rem', textAlign: 'center', color: '#aaa' }}>No notes found for the selected filters.</td></tr>
                : rows.map((r, i) => (
                  <tr key={i} onClick={() => goToOrder(r.orderId)} style={{ cursor: 'pointer' }}>
                    <td style={{ fontWeight: 500 }}>{r.authorName}</td>
                    <td style={{ fontWeight: 600, color: '#185FA5' }}>#{r.fileNumber}</td>
                    <td>{r.loanNumber||'—'}</td>
                    <td style={{ fontWeight: 500 }}>{r.lastName||'—'}</td>
                    <td>{r.lender||'—'}</td>
                    <td>{r.broker||'—'}</td>
                    <td style={{ color: '#888', fontSize: 12, whiteSpace: 'nowrap' }}>{fmtDate(r.noteDate)}</td>
                    <td style={{ lineHeight: 1.5, maxWidth: 400 }}>{r.noteBody}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Call Report ────────────────────────────────────────────────────────────────
export function CallReportPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { crFilters, setCRFilters } = useReportFilters();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const defaultCRFilters = { orderFrom: daysAgoISO(30), orderTo: todayISO(), statusId: '' };
  const [filters, setFilters] = useState(crFilters || defaultCRFilters);
  const [appliedFilters, setAppliedFilters] = useState(crFilters || defaultCRFilters);

  function setF(k, v) { setFilters(f => ({ ...f, [k]: v })); }

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { orderFrom: appliedFilters.orderFrom, orderTo: appliedFilters.orderTo };
      if (appliedFilters.statusId) params.statusId = appliedFilters.statusId;
      setRows(await adminApi.getCallReport(params));
    }
    catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [appliedFilters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  function handleApply() {
    setCRFilters({ ...filters });
    setAppliedFilters({ ...filters });
  }

  function handleReset() {
    setCRFilters(null);
    setFilters(defaultCRFilters);
    setAppliedFilters({ ...defaultCRFilters });
  }

  function goToOrder(orderId) {
    setCRFilters({ ...filters });
    navigate(`/orders/${orderId}`, { state: { from: location.pathname } });
  }

  async function handleExport() {
    const data = [['File #','Loan Number','Last Name','Lender','Broker','State','Date Ordered','Closing Date','Revenue','Status','Loan Type','Loan Amount','Property Type','Assigned To','Rate','Jumbo','MI','Lien Status','Purpose']];
    rows.forEach(r => data.push([`#${r.fileNumber}`,r.loanNumber||'',r.lastName||'',r.lender||'',r.broker||'',r.state||'',fmtDate(r.dateOrdered),fmtDate(r.closingDate),r.revenue||'',r.statusName||'',r.loanTypeName||'',r.loanAmount||'',r.propertyType||'',r.assignedTo||'',r.rate||'',r.jumbo != null ? (r.jumbo ? 'Yes':'No'):'',r.mi != null ? (r.mi ? 'Yes':'No'):'',r.lienStatusName||'',r.purpose||'']));
    await exportToExcel(data, 'CallReport', 'Call Report');
  }

  return (
    <div>
      <TopNav />
      <div className="page-content">
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: '1.25rem', flexWrap: 'wrap', gap: 12 }}>
          <h2>Call report — loan files</h2>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
            <FilterGroup label="Order date">
              <input type="date" value={filters.orderFrom} onChange={e => setF('orderFrom', e.target.value)} style={{ width: 140, fontSize: 12, padding: '5px 8px' }} />
              <span style={{ fontSize: 12, color: '#888' }}>to</span>
              <input type="date" value={filters.orderTo} onChange={e => setF('orderTo', e.target.value)} style={{ width: 140, fontSize: 12, padding: '5px 8px' }} />
            </FilterGroup>
            <FilterGroup label="Status">
              <select value={filters.statusId} onChange={e => setF('statusId', e.target.value)} style={{ fontSize: 12, padding: '5px 8px', width: 'auto' }}>
                <option value="">All</option>
                <option value="1">Open</option><option value="2">Closed</option>
                <option value="3">Cancelled</option><option value="4">Hold</option>
              </select>
            </FilterGroup>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary btn-sm" onClick={handleApply}>Apply</button>
              <button className="btn btn-sm" onClick={handleReset}>Reset</button>
              <button className="btn btn-sm" onClick={handleExport} style={{ display: 'flex', alignItems: 'center', gap: 5 }}><DownloadIcon /> Export</button>
            </div>
          </div>
        </div>

        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table className="report-table" style={{ minWidth: 1750 }}>
              <thead><tr>
                {['File #','Loan number','Last name','Lender','Broker','State','Date ordered','Closing date','Revenue','Status','Loan type','Loan amount','Property type','Assigned to','Rate','Jumbo','MI','Lien status','Purpose'].map(h => <th key={h}>{h}</th>)}
              </tr></thead>
              <tbody>
                {loading ? <tr><td colSpan={19} style={{ padding: '2rem', textAlign: 'center', color: '#888' }}>Loading...</td></tr>
                : rows.length === 0 ? <tr><td colSpan={19} style={{ padding: '2rem', textAlign: 'center', color: '#aaa' }}>No loan files match the selected filters.</td></tr>
                : rows.map(r => (
                  <tr key={r.fileNumber} onClick={() => goToOrder(r.orderId)} style={{ cursor: 'pointer' }}>
                    <td style={{ fontWeight: 600, color: '#185FA5' }}>#{r.fileNumber}</td>
                    <td>{r.loanNumber||'—'}</td>
                    <td style={{ fontWeight: 500 }}>{r.lastName||'—'}</td>
                    <td>{r.lender||'—'}</td>
                    <td>{r.broker||'—'}</td>
                    <td>{r.state||'—'}</td>
                    <td>{fmtDate(r.dateOrdered)}</td>
                    <td>{fmtDate(r.closingDate)}</td>
                    <td>{fmtCurrency(r.revenue)}</td>
                    <td><span className={`badge ${statusClass(r.statusName)}`}>{r.statusName}</span></td>
                    <td>{r.loanTypeName||'—'}</td>
                    <td>{fmtCurrency(r.loanAmount)}</td>
                    <td>{r.propertyType||'—'}</td>
                    <td style={{ color: '#888' }}>{r.assignedTo||'—'}</td>
                    <td>{r.rate ? `${r.rate}%` : '—'}</td>
                    <td>{r.jumbo != null ? (r.jumbo ? 'Yes' : 'No') : '—'}</td>
                    <td>{r.mi != null ? (r.mi ? 'Yes' : 'No') : '—'}</td>
                    <td>{r.lienStatusName||'—'}</td>
                    <td>{r.purpose||'—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Shared helpers ─────────────────────────────────────────────────────────────
function FilterGroup({ label, children }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#888', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</label>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>{children}</div>
    </div>
  );
}

function DownloadIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
      <polyline points="7 10 12 15 17 10"/>
      <line x1="12" y1="15" x2="12" y2="3"/>
    </svg>
  );
}
